// Telemetry noise filters shared by the client initializer (AppInsightsProvider) and the
// server preload (otel-preload.cjs, via server-telemetry.js) so the two can't drift.
// Plain JS so the CommonJS preload can require it; keep it free of browser/Node-only APIs.

// Hostnames considered local: no telemetry for local traffic
export const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '::1', '[::1]'];

// Request paths that never produce telemetry: static assets (including Next's own
// /_next/static chunks and fonts) and i18n chatter, the app's and the sample harness's
// (public/aps-sample/simple.html). /_next/image stays tracked: the image optimizer does
// real work and can fail.
export const IGNORED_PATHS = ['/favicon.ico', '/locales/', '/aps-sample/locales/', '/font/', '/_next/static/'];

// Matches a bare hostname or a Host header value (hostname plus optional port)
export function isLocalHost(host) {
    if (!host) {
        return false;
    }
    const normalized = host.toLowerCase();
    return LOCAL_HOSTNAMES.some(
        (local) => normalized === local || normalized.startsWith(`${local}:`)
    );
}

// Prefix of this app's request path only: a query string or a deeper path segment
// mentioning an asset path must not match
export function isIgnoredPath(requestUrl) {
    const pathname = (requestUrl ?? '').split('?')[0];
    return IGNORED_PATHS.some((ignored) => pathname.startsWith(ignored));
}

// Full URL as the client SDK reports it (page views, dependencies), checked with the
// server's host and path rules. The path rules describe this app's own routes, so they
// apply only to URLs on ownOrigin (relative URLs count as on it); a backend or CDN URL
// that happens to contain /locales/ is kept.
export function isIgnoredUrl(url, ownOrigin) {
    if (!url) {
        return false;
    }
    let parsed;
    try {
        parsed = new URL(url, ownOrigin);
    } catch {
        // Relative, with no origin to resolve against
        return isIgnoredPath(url);
    }
    return isLocalHost(parsed.host) || (parsed.origin === ownOrigin && isIgnoredPath(parsed.pathname));
}

// Client SDK telemetry item checked with isIgnoredUrl. The SDK puts the URL in `uri` for
// page views and `target` for dependencies; `url` is only a fallback.
export function isIgnoredTelemetryItem(item, ownOrigin) {
    const data = item?.baseData;
    return isIgnoredUrl(data?.uri ?? data?.target ?? data?.url, ownOrigin);
}
